/**
 * SkinVertexData — the plain-data carrier for a skinned mesh's bind-space
 * vertices and their 4-influence skin palette, as ingress ships them
 * (glTF JOINTS_0/WEIGHTS_0 semantics). ONE responsibility: define the shape the
 * ADR-021 weight measurement (weights/chest-integrity) and repair
 * (weights/bound-influence) consume, plus its fail-loud structural gate. Pure
 * data — no mesh object, no engine, no three (ADR-002 §6): the adapter extracts
 * these arrays from whatever skinning representation it has and seats repaired
 * arrays back itself.
 *
 * Joint identity is carried by NAME (`jointNames[paletteIndex]` = the rig's own
 * bone name), because the consumers resolve bones through the RigDescriptor
 * mapping (weights/chest-bones) and rig bone names are the rig's identifiers
 * (ADR-003 §2b) — a bare palette index means nothing across representations.
 *
 * Fail-loud (LAWS "fail loudly over silent fallback"): mismatched array
 * lengths, an out-of-range joint index, or a non-finite/negative weight throw
 * {@link SkinDataError} with every issue found — never a silent partial read.
 *
 * Depends on: ir/errors. Depended on by: weights/chest-integrity,
 * weights/bound-influence, weights/support-field, kernel index.
 */
import { KernelError } from '../ir/errors.js';

/** glTF skins carry four influences per vertex; so does three.js. */
export const INFLUENCES_PER_VERTEX = 4;

/** A skinned mesh's bind-space vertices + skin palette, as plain flat arrays. */
export interface SkinVertexData {
  /** Flat `[x,y,z,...]` BIND-space vertex positions. */
  readonly positions: ArrayLike<number>;
  /** Four palette indices per vertex, indexing {@link jointNames}. */
  readonly jointIndices: ArrayLike<number>;
  /** Four weights per vertex, aligned with {@link jointIndices}. */
  readonly jointWeights: ArrayLike<number>;
  /** Palette index → the rig's own bone name (the skeleton's joint order). */
  readonly jointNames: readonly string[];
}

/** Structurally broken skin data — a producer/caller bug, fail-loud. */
export class SkinDataError extends KernelError {
  constructor(readonly issues: readonly string[]) {
    super(
      `Skin data is structurally invalid (${issues.length} issue(s)):\n` +
        issues.map((i) => `  - ${i}`).join('\n'),
    );
  }
}

/** Vertex count implied by the positions array (validated by {@link validateSkinData}). */
export function skinVertexCount(skin: SkinVertexData): number {
  return Math.floor(skin.positions.length / 3);
}

/** Throw {@link SkinDataError} on any structural violation, reporting every issue. */
export function validateSkinData(skin: SkinVertexData): void {
  const issues: string[] = [];
  const posLen = skin.positions.length;
  if (posLen === 0) issues.push('positions is empty — no vertices to measure');
  if (posLen % 3 !== 0) issues.push(`positions length ${posLen} is not a multiple of 3`);
  const n = Math.floor(posLen / 3);
  const k = INFLUENCES_PER_VERTEX;
  if (skin.jointIndices.length !== n * k) {
    issues.push(`jointIndices length ${skin.jointIndices.length} ≠ ${n} vertices × ${k}`);
  }
  if (skin.jointWeights.length !== n * k) {
    issues.push(`jointWeights length ${skin.jointWeights.length} ≠ ${n} vertices × ${k}`);
  }
  if (skin.jointNames.length === 0) issues.push('jointNames is empty — no palette to resolve against');
  let badIndex = 0;
  for (let i = 0; i < skin.jointIndices.length; i += 1) {
    const j = skin.jointIndices[i]!;
    if (!Number.isInteger(j) || j < 0 || j >= skin.jointNames.length) badIndex += 1;
  }
  if (badIndex > 0) {
    issues.push(`jointIndices has ${badIndex} value(s) out of range for a ${skin.jointNames.length}-joint palette`);
  }
  let badWeight = 0;
  for (let i = 0; i < skin.jointWeights.length; i += 1) {
    const w = skin.jointWeights[i]!;
    if (!Number.isFinite(w) || w < 0) badWeight += 1;
  }
  if (badWeight > 0) issues.push(`jointWeights has ${badWeight} non-finite or negative value(s)`);
  let badPos = 0;
  for (let i = 0; i < posLen; i += 1) {
    if (!Number.isFinite(skin.positions[i]!)) badPos += 1;
  }
  if (badPos > 0) issues.push(`positions has ${badPos} non-finite coordinate(s)`);
  if (issues.length > 0) throw new SkinDataError(issues);
}
